<template>
  <ModalBase :show-modal="!loading" @close-modal="close" unpadded modal-modal class="gk-premium-modal mini-premium">
    <template v-slot:maincontent>
      <div class="premium-modal__wrap">
        <!-- IMAGE -->
        <div class="premium-modal__cta-image">
          <div style="position: absolute; top: 1.5rem; left: 1.5rem">
            <GkBadge :text="$gettext('Premium')" icon="fa-solid fa-crown" type="premium" variant="base" size="lg" />
          </div>
          <img :src="imageSrc" alt="" />
        </div>
        <!-- CONTENT -->
        <div class="premium-modal__html-content-wrap" :class="{ 'os-overflown-content': !scrollEnd }">
          <!-- HEADER -->
          <div v-if="headerHtml" class="premium-modal__html-header" v-html="headerHtml" />

          <!-- MAIN -->
          <div class="content-wrap-shadow">
            <overlay-scrollbars ref="premiumModalScrollbar" :options="scrollBarsOptions">
              <div class="premium-modal__html-content">
                <div v-if="mainHtml" ref="mainHtmlContainer" class="content" v-html="mainHtml" />
                <PremiumModalDefaultContent v-else-if="!accountantsModal" />
                <PremiumModalDefaultContentAccountants v-else-if="accountantsModal" />
              </div>
            </overlay-scrollbars>
            <div class="overflown-content-shadow-wrap">
              <div class="overflown-content-shadow"></div>
            </div>
          </div>

          <!-- FOOTER -->
          <div v-if="!accountantsModal" class="premium-modal__html-footer">
            <!-- <div v-html="priceHtml" /> -->
            <div v-if="footerHtml" v-html="footerHtml" />
            <div v-else>
              <GekkoButton
                data-analytics-click="Subscription modal v3: upgrade"
                :href="Urls['subscriptions:manage']()"
                :text="$gettext('Upgrade now')"
                class="w-100"
                size="xl"
              />
            </div>
            <div class="pt-3">
              <GekkoButton
                data-analytics-click="Subscription modal v3: pricing"
                :href="Urls['pages']('pricing')"
                :text="$gettext('See all Premium features')"
                target="_blank"
                variant="link"
                class="w-100 p-0"
              />
            </div>
          </div>
          <div v-else class="premium-modal__html-footer">
            <GekkoButton
              v-if="premiumModalCode != 'web_accountants_exceeded'"
              data-analytics-click="Accountants Subscription modal: upgrade"
              @click="upgradeAccountantsSubscription"
              :text="$gettext('Upgrade now')"
              class="w-100"
              size="xl"
            />
          </div>
        </div>
      </div>
    </template>
  </ModalBase>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import GekkoButton from '@/components/GekkoButton'
import GkBadge from '@/components/GkBadge.vue'
import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import PremiumModalDefaultContent from '@/components/ui/PremiumModalDefaultContent'
import PremiumModalDefaultContentAccountants from '@/components/ui/PremiumModalDefaultContentAccountants'

import PremiumService from '@/store/services/premium.service.js'
import PlanService from '@/store/services/plan.service'

export default {
  name: 'PremiumModal',
  components: {
    GekkoButton,
    GkBadge,
    ModalBase,
    TitleBar,
    PremiumModalDefaultContent,
    PremiumModalDefaultContentAccountants
  },
  props: {
    accountantsModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalHtml: null,
      headerHtml: null,
      mainHtml: null,
      priceHtml: null,
      footerHtml: null,
      image: null,
      loading: true,
      scrollEnd: true,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
        callbacks: {
          onScroll: this.handleScroll,
        },
      },
      defaultImage: '/static/img/default-premium-modal.png',
    }
  },
  computed: {
    ...mapGetters({
      premiumModalCode: 'getPremiumModalCode',
      getProductByTierId: 'subscriptionStore/getProductByTierId',
      getAvailableTiers: 'subscriptionStore/getAvailableTiers',
      billingSettings: 'subscriptionStore/getBillingSettings',
      getCompanyId: 'main/getCompanyId',
      currentTier: 'subscriptionStore/getCurrentTier'
    }),
    imageSrc() {
      return this.image ? this.image : this.defaultImage
    },
  },
  watch: {
    premiumModalCode() {
      this.setupData()
    },
  },
  created () {
    if (this.accountantsModal) {
      this.loadSubscriptionsMainData()
        .then(() => {
          if (this.premiumModalCode) {
            this.newTier = this.getAvailableTiers.find((tier) => 'web_accountants_' + tier.tier_slug === this.premiumModalCode)
            this.newProduct = this.getProductByTierId(this.newTier.id)
          }
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  mounted() {
    document.body.classList.add('modal-on')
    this.loading = true
    this.setupData()
  },
  methods: {
    ...mapMutations({
      togglePremiumModal: 'togglePremiumModal',
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    ...mapActions({
      storeUpdateSubscription: 'subscriptionStore/updateSubscription',
      loadSubscriptionsMainData: 'subscriptionStore/loadSubscriptionsMainData',
    }),
    setupData() {
      PremiumService.list({ code: this.premiumModalCode })
        .then((response) => {
          this.modalHtml = response.data[0].content
          const { headerContent, mainContent, priceContent, footerContent } = this.processHtml(this.modalHtml)
          this.headerHtml = headerContent
          this.mainHtml = mainContent
          this.priceHtml = priceContent
          this.footerHtml = footerContent
          this.image = response.data[0].image
        })
        .catch((error) => {
          console.log('error loading premium modal', error)
          this.resetModalData()
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.osInstance = this.$refs.premiumModalScrollbar.osInstance()
            this.handleScroll() // check if content shadow should be displayed at init
            this.mountComponents('gk-premium-badge', 'GkBadge', 'mainHtmlContainer')
          })
        })
    },
    mountComponents(placeholder_name, component_name, html_container_ref) {
      if (!this.$refs[html_container_ref]) return
      const placeholders = this.$refs[html_container_ref].querySelectorAll(placeholder_name)
      const ComponentConstructor = this.$options.components[component_name]
      placeholders.forEach((placeholder) => {
        const propsData = {}
        for (const attr of placeholder.attributes) {
          let val = attr.value
          try {
            val = JSON.parse(val)
          } catch (e) {
            // keep as string if not valid JSON
          }
          propsData[attr.name] = val
        }
        const ComponentInstance = new ComponentConstructor({
          propsData,
          store: this.$store,
        })
        ComponentInstance.$mount()
        placeholder.replaceWith(ComponentInstance.$el)
      })
    },
    close() {
      this.togglePremiumModal(null)
    },
    processHtml(htmlString) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlString, 'text/html')

      const priceSection = doc.querySelector('.popup-price-section')
      let priceContent = ''
      if (priceSection) {
        priceContent = priceSection.outerHTML
        priceSection.remove()
      }

      // Extract and remove .header
      const header = doc.querySelector('.header')
      let headerContent = ''
      if (header) {
        headerContent = header.outerHTML
        header.remove()
      }
      // Extract and remove .footer
      const footer = doc.querySelector('.footer')
      let footerContent = ''
      if (footer) {
        footerContent = footer.outerHTML
        footer.remove()
      }

      // Remaining HTML
      const remainingHtml = doc.body.innerHTML

      return {
        headerContent,
        mainContent: remainingHtml,
        priceContent,
        footerContent,
      }
    },
    resetModalData() {
      this.modalHtml = null
      this.headerHtml = null
      this.mainHtml = null
      this.priceHtml = null
      this.footerHtml = null
      this.image = null
    },
    handleScroll() {
      if (this.osInstance && this.$refs.premiumModalScrollbar.$el.classList.contains('os-host-overflow-y')) {
        const scrollEndThreshold = 0.99
        this.scrollEnd = this.osInstance.scroll().ratio.y >= scrollEndThreshold
      } else {
        this.scrollEnd = true
      }
    },
    upgradeAccountantsSubscription() {
      if (this.newTier.tier_slug !== 'scale_up' || this.currentTier?.tier_slug === 'starter') {
        this.upgradeAccountantPlan(this.newTier.id)
      } else {
        this.createAccountantPlan(this.newTier.id)
      }
    },
    upgradeAccountantPlan(tierId) {
      this.setWaitingForResponse(true)
      this.storeUpdateSubscription({
        router: this.$router,
        payload: {
          yearlyCycle: this.billingSettings.renewal_months === 12,
          updatedCycle: false,
          tier: tierId,
          updatedTier: true,
          uncancelTier: false,
        },
      })
        .then(() => {
          this.loading = true
          this.loadSubscriptionsMainData().then(() => {
            if (this.updatedCycle) {
              this.$store.commit('setAlert', {
                title: this.$gettext('Subscription Updated'),
                text: this.$gettext(
                  'Your subscription plan has been updated. Now you can send more invitations and connect to new customers!'
                ),
                variant: 'success',
                duration: 4,
              })
            }
            this.loading = false
          })
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('We could not process your payment details. Try again later or contact support.'),
            variant: 'danger',
            duration: 4,
          })
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    createAccountantPlan(tierId) {
      PlanService.create({
        company_id: this.getCompanyId,
        months: this.billingSettings.renewal_months,
        tier: tierId,
      }).then((response) => {
        var a_transaction_id = response.data
        const url = this.Urls['subscriptions:confirm_payment'](a_transaction_id)
        window.location.assign(url)
      })
    },
  },
}
</script>
<style lang="scss">
</style>
